import { useState } from 'react';
import { Box, Button, Collapse, Divider, Link, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HomeIcon from '@mui/icons-material/Home';

const CURRENT_YEAR = new Date().getFullYear();

const STATE_LICENSES = [
  'Alabama (Consumer Credit License #23184)',
  'Arizona (Mortgage Banker License #BK-1027005)',
  'Arkansas (Combination Mortgage Banker-Broker-Servicer License #43004)',
  'California (DFPI Financing Law License #603J733)',
  'Colorado (Mortgage Company Registration)',
  'Florida (Mortgage Lender License #MLD506)',
  'Georgia (Mortgage Lender License #66436)',
  'Idaho (Mortgage Broker/Lender License #MBL-2080034381)',
  'Illinois (Residential Mortgage License #MB.6760568)',
  'Indiana (Mortgage Lending License (DFI) #11139)',
  'Iowa (Mortgage Banker License #2008-0093)',
  'Kansas (Mortgage Company License #MC.0025426)',
  'Kentucky (Mortgage Company License #MC23287)',
  'Louisiana (Residential Mortgage Lending License)',
  'Maine (Supervised Lender License #34381)',
  'Maryland (Mortgage Lender License #34381)',
  'Massachusetts (Mortgage Lender License #ML34381)',
  'Michigan (1st Mortgage Broker/Lender/Servicer Registrant #FR0020432)',
  'Minnesota (Residential Mortgage Originator License #MN-MO-40134678)',
  'Mississippi (Mortgage Lender License #34381)',
  'Missouri (Mortgage Company License #34381)',
  'Nebraska (Mortgage Banker License #2004)',
  'Nevada (Mortgage Company License #4944)',
  'New Hampshire (Mortgage Banker License #34381MB)',
  'New Jersey (Residential Mortgage Lender License)',
  'New Mexico (Mortgage Loan Company License)',
  'North Carolina (Mortgage Lender License #L-186719)',
  'North Dakota (Residential Mortgage Lender #ML104863)',
  'Ohio (Residential Mortgage Lending Act Certificate #RM.850040.000)',
  'Oklahoma (Mortgage Lender License #ML011045)',
  'Oregon (Mortgage Lending License #34381)',
  'Pennsylvania (Mortgage Lender License #99512)',
  'South Carolina (Mortgage Lender/Servicer License (BFI) #MLS-34381)',
  'South Dakota (Mortgage Lender License #34381.ML)',
  'Tennessee (Mortgage License #34381)',
  'Texas (Mortgage Banker Registration (SML))',
  'Virginia (Lender License #MC-7052)',
  'Washington (Consumer Loan Company License #CL-34381)',
  'Wisconsin (Mortgage Banker License #34381BA)',
];

const FooterText = ({ children, sx, ...rest }) => (
  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, display: 'block', mb: 1, ...sx }} {...rest}>
    {children}
  </Typography>
);

const ComplianceFooter = ({
  companyName = 'United Fidelity Funding Corp.',
  nmlsId = '34381',
  contactPhone,
}) => {
  const [showLegal, setShowLegal] = useState(false);
  const [showLicenses, setShowLicenses] = useState(false);
  const displayName = companyName || 'United Fidelity Funding Corp.';
  const displayNmls = nmlsId || '34381';
  const nmlsUrl = `https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/${displayNmls}`;

  return (
    <Box component="footer" sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', py: 1.5, px: 3, mt: 'auto', '@media print': { display: 'none' } }}>
      {/* Always-visible summary row */}
      <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
          <Typography variant="caption" fontWeight={700} color="text.primary">{displayName}</Typography>
          <Typography variant="caption" color="text.secondary">NMLS #{displayNmls}</Typography>
          {contactPhone && (
            <>
              <Typography variant="caption" color="text.secondary">·</Typography>
              <Link href={`tel:${contactPhone.replace(/\D/g, '')}`} variant="caption" color="secondary">
                {contactPhone}
              </Link>
            </>
          )}
          <Typography variant="caption" color="text.secondary">·</Typography>
          <Link href={nmlsUrl} target="_blank" rel="noopener noreferrer" variant="caption" color="secondary">
            NMLS Consumer Access
          </Link>
          <Typography variant="caption" color="text.secondary">·</Typography>
          <Typography variant="caption" color="text.secondary">
            &copy; {CURRENT_YEAR} All rights reserved.
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" color="text.secondary">Equal Housing Lender</Typography>
            <HomeIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
          </Stack>
          <Button
            size="small"
            color="inherit"
            onClick={() => setShowLegal(!showLegal)}
            endIcon={showLegal ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ fontSize: '0.72rem', color: 'text.secondary', p: 0, minWidth: 0, fontWeight: 500 }}
          >
            Legal & Licensing
          </Button>
        </Stack>
      </Stack>

      {/* Expanded legal detail */}
      <Collapse in={showLegal}>
        <Divider sx={{ my: 1.5 }} />
        <FooterText>
          This is not an offer to enter into an agreement. Not all customers will qualify.
          Information, rates, and programs are subject to change without notice.
          All products are subject to credit and property approval.
          Other restrictions and limitations may apply.
        </FooterText>

        <FooterText>
          {displayName} is committed to fair lending practices in accordance with the
          Equal Credit Opportunity Act (ECOA) and the Fair Housing Act. We do not discriminate on the
          basis of race, color, religion, national origin, sex, marital status, age, disability,
          familial status, or any other characteristic protected by federal, state, or local law.
        </FooterText>

        <FooterText>
          Your privacy is important to us. We collect and protect personal information in accordance with
          the Gramm-Leach-Bliley Act (GLBA) and applicable state privacy laws. Sensitive borrower
          information accessed through this portal is encrypted, logged, and subject to our
          information security program. Unauthorized access or misuse of borrower data is strictly
          prohibited and may result in civil and criminal penalties.
        </FooterText>

        <FooterText>
          All mortgage loan originators are required to be registered or licensed in accordance with the
          Secure and Fair Enforcement for Mortgage Licensing Act of 2008 (SAFE Act).
          Verify your loan originator&apos;s license at{' '}
          <Link href="https://www.nmlsconsumeraccess.org" target="_blank" rel="noopener noreferrer" color="secondary" variant="caption">
            nmlsconsumeraccess.org
          </Link>.
        </FooterText>

        {/* State Licensing */}
        <Box>
          <Button
            size="small"
            color="inherit"
            onClick={() => setShowLicenses(!showLicenses)}
            endIcon={showLicenses ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ fontSize: '0.72rem', color: 'text.secondary', textDecoration: 'underline', p: 0, minWidth: 0 }}
          >
            State Licensing ({STATE_LICENSES.length} states)
          </Button>
          <Collapse in={showLicenses}>
            <Box sx={{ mt: 1, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
              <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                {displayName}, NMLS #{displayNmls}, is licensed in the following states:
              </Typography>
              <Box sx={{ columnCount: 2, columnGap: '2rem' }}>
                {STATE_LICENSES.map((lic, i) => (
                  <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block', breakInside: 'avoid', mb: '2px' }}>
                    {lic}{i < STATE_LICENSES.length - 1 ? ';' : '.'}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ComplianceFooter;
